import { useState } from 'react'
import { Check, Copy, RefreshCw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ChatMessageContent from '@/components/content/ChatMessageContent'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import type { RecipeExecutionStatus } from '@/services/api/workflows'
import { resolveExecutionResultBody } from '@/services/chat/executionDetailView'
import RecipeExecutionUsageSummaryCard from './RecipeExecutionUsageSummaryCard'

interface RecipeExecutionResultPanelProps {
  execution?: RecipeExecutionStatus | null
  executionId?: string
  recipeName?: string
  resultData?: Record<string, any>
  messageContent?: string
  expired?: boolean
  isLoading?: boolean
  isRefreshing?: boolean
  error?: string | null
  onRefresh?: () => void
  onClose?: () => void
}

export default function RecipeExecutionResultPanel({
  execution,
  executionId,
  recipeName,
  resultData: directResultData,
  messageContent,
  expired = false,
  isLoading = false,
  isRefreshing = false,
  onRefresh,
  onClose,
}: RecipeExecutionResultPanelProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const body = resolveExecutionResultBody({
    resultData: directResultData,
    executionResult: execution?.data ?? execution?.result,
    messageContent,
    expired,
  })
  const resultData = body.kind === 'result' ? body.data : null
  const hasResult = body.kind === 'result'

  const handleCopyResult = async () => {
    if (!hasResult) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(resultData, null, 2))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch (copyError) {
      console.error('Unable to copy result', copyError)
    }
  }

  const executionLabel =
    recipeName ||
    execution?.workflow_name ||
    execution?.workflow_id ||
    executionId ||
    t('common.run')

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-foreground">
              {t('executionResult.title')}
            </p>
            <h3 className="truncate text-sm font-semibold text-foreground">
              {executionLabel}
            </h3>
          </div>
          <div className="flex items-center gap-1.5">
            {onRefresh && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onRefresh}
                disabled={isLoading || isRefreshing}
                title={t('executionResult.refresh')}
              >
                <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void handleCopyResult()}
              disabled={!hasResult}
              title={t('executionResult.copy')}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose} title={t('common.close')}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {!isLoading && execution?.usage_summary && (
          <div className="mb-4">
            <RecipeExecutionUsageSummaryCard summary={execution.usage_summary} />
          </div>
        )}
        {isLoading && body.kind !== 'result' ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('executionResult.loading')}
          </div>
        ) : body.kind === 'expired' ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('executionResult.expired')}
          </div>
        ) : body.kind === 'message' ? (
          <div className="text-sm text-foreground">
            <ChatMessageContent content={body.content} />
          </div>
        ) : !hasResult ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('executionResult.empty')}
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(resultData as Record<string, any>).map(([key, value]) => (
              <div key={key} className="rounded-xl border border-border bg-card p-3">
                <div className="mb-2 text-[13px] font-semibold text-foreground">
                  {key}
                </div>
                {typeof value === 'object' && value !== null ? (
                  <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-muted p-3 text-xs text-foreground">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                ) : (
                  <div className="text-sm text-foreground break-words">{String(value)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
